<template>
  <WdsStack direction-column gap-custom="2.5rem" class="tw-py-2">
    <BaseSection>
      <BaseSectionHeader :header-text="$t('Hiring')" />

      <BaseSectionBody>
        <BaseFieldGroup
          :label="$t('Required workers')"
          :helper-text="$t('How many workers are required for this job?')"
        >
          <WdsInput
            v-model="computedRequiredWorkers"
            class="tw-text-right"
            type="number"
            :error="getErrorMessage(validationStore, 'requiredWorkers')"
          />
        </BaseFieldGroup>
        <BaseFieldGroup
          :label="$t('Job owners')"
          :helper-text="$t('Who is responsible for hiring this job?')"
        >
          <div class="tw-w-full">
            <!-- The default value below for avatars is used to avoid the component "jumping" when job owners are removed / added -->
            <WdsAvatarGroup
              sm
              :max-avatars="15"
              class="tw-float-right tw-mb-sm"
              :class="{ 'tw-invisible': avatars.length === 0 }"
              :avatars="avatars.length ? avatars : [{ path: '' }]"
            />
            <WdsSelect
              v-model="jobStore.form.jobOwners"
              multi-select
              :list="jobOwners"
              :placeholder="$t('Add/remove owners...')"
              prefix-icon="search"
              searchable
              class="tw-w-full"
            >
              <template #option="{ option }">
                <WdsStack align-center gap-sm>
                  <WdsAvatar xs :path="getAvatarPath(option.avatar, option.value)" />
                  <div>{{ option.label }}</div>
                </WdsStack>
              </template>
              <template #header="{ options }">
                <span v-if="options.length">
                  {{ $t(':num selected', { num: options.length }) }}
                </span>
                <span v-else>
                  {{ $t('Add/remove owners...') }}
                </span>
              </template>
            </WdsSelect>
          </div>
        </BaseFieldGroup>

        <WdsAlert info bordered>
          {{ $t('Keep yourself as a job owner to receive notifications related to this job.') }}
        </WdsAlert>
      </BaseSectionBody>
    </BaseSection>

    <BaseSection v-if="jobStore.persistedJob?.viewerCanDelete">
      <BaseSectionHeader :header-text="$t('Admin controls')" />

      <BaseSectionBody>
        <BaseFieldGroup :label="$t('Delete job')" :helper-text="$t('Permanently delete this job')">
          <WdsButton error-subtle type="button" @click="toggleDeleteConfirmationModal(true)">
            {{ $t('Delete Job') }}
          </WdsButton>
          <WdsModal
            :title="$t('Delete this job?')"
            :show="showDeleteConfirmationModal"
            no-close-icon
            @cancel="toggleDeleteConfirmationModal(false)"
          >
            <div class="tw-text-gray-600 -tw-mt-7">
              {{ $t('This job will be permanently deleted') }}
            </div>
            <WdsStack direction-row align-center gap-sm class="tw-float-right tw-mt-6">
              <WdsSpinner v-if="updateJobLoading" indeterminate data-test="deleting-job-spinner" />

              <WdsButton :disabled="updateJobLoading" @click="toggleDeleteConfirmationModal(false)">
                {{ $t('Back to editor') }}
              </WdsButton>
              <WdsButton error :disabled="updateJobLoading" @click="removeJob">
                {{ $t('Delete job') }}
              </WdsButton>
            </WdsStack>
          </WdsModal>
        </BaseFieldGroup>
      </BaseSectionBody>
    </BaseSection>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsStack,
  WdsButton,
  WdsSelect,
  WdsAvatar,
  WdsModal,
  WdsAvatarGroup,
  WdsAlert,
  WdsInput,
  WdsSpinner,
} from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'

import { useFragment } from '@/gql'
import { $t } from '@/composables/translation'
import BaseSection from '@/components/BaseSection.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import { getAvatarPath } from '@/utils/avatar'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import NetworkError from '@/utils/notification/NetworkError'

import { getErrorMessage } from '@/components/Job/functions/getErrorMessage'
import { JobFormDataFragment } from '@/components/Job/graphql/fragments'
import { UpdateJobMutation } from '@/components/Job/graphql/mutations'
import { TeamMembersAndOwnersQuery } from '@/components/Job/graphql/queries'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'

const jobStore = useJobFormStore()
const validationStore = useValidationStore()

const showDeleteConfirmationModal = ref(false)

const toggleDeleteConfirmationModal = (shouldShow: boolean) => {
  showDeleteConfirmationModal.value = shouldShow
}

const avatars = computed(() =>
  jobStore.form.jobOwners.map((jobOwner) => ({
    path: getAvatarPath(jobOwner.avatar, jobOwner.value),
  })),
)

const { result } = useQuery(
  TeamMembersAndOwnersQuery,
  {
    companyId: currentAccount.globalId,
    first: PER_PAGE_MAXIMUM,
  },
  {
    clientId: 'publicApi',
  },
)

const { loading: updateJobLoading, mutate: mutateUpdateJob } = useMutation(UpdateJobMutation, {
  clientId: 'publicApi',
})

const jobOwners = computed(() =>
  result.value?.company?.teamMembersAndOwners?.data?.map(({ id, name, avatar }) => ({
    label: name,
    value: id,
    avatar,
  })),
)

const computedRequiredWorkers = computed({
  get: () => jobStore.form.requiredWorkers.toString(),
  set: (requiredWorkers) => {
    jobStore.form.requiredWorkers = Number(requiredWorkers)
  },
})

const removeJob = async () => {
  try {
    const updateResult = await mutateUpdateJob({
      input: {
        id: jobStore.persistedJob?.id || '',
        removed: true,
      },
    })

    const data = updateResult?.data?.updateJob

    if (data) {
      const job = useFragment(JobFormDataFragment, data)

      jobStore.loadPersistedJob(job)
    }

    toggleDeleteConfirmationModal(false)
  } catch (error: any) {
    new NetworkError({
      ex: error,
    }).dispatch()
  }
}
</script>

<style scoped lang="scss">
:deep(.wds-select-list-container) {
  @apply tw-max-h-52;
}
</style>
